import React from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { useMediaQuery } from "@/lib/useMediaQuery";

/**
 * On MOBILE: renders a navigation card that links to a dedicated route (`to`).
 * On DESKTOP: renders children inline (untouched).
 * This gives us proper back-button behaviour on mobile phones.
 */

// La tarjeta muestra la imagen en una caja de 96 px. Servir el .webp original
// (hasta 650 KB) ahi era el mayor desperdicio de la home en movil: usamos las
// miniaturas de /assets/thumb generadas por scripts/optimize-thumbs.sh.
const thumbBase = (src) => src.replace("/assets/", "/assets/thumb/").replace(/\.webp$/, "");
export default function MobileFold({ id, to, eyebrow, title, teaser, icon: Icon, image, imageAlt, children }) {
  const isMobile = useMediaQuery("(max-width: 639px)");

  if (!isMobile) return <>{children}</>;

  const CardInner = (
    <>
      <div className="w-24 shrink-0 relative bg-[var(--faro-blue)]">
        {image ? (
          <picture>
            <source type="image/avif" srcSet={`${thumbBase(image)}-192.avif 192w, ${thumbBase(image)}-384.avif 384w`} sizes="96px" />
            <source type="image/webp" srcSet={`${thumbBase(image)}-192.webp 192w, ${thumbBase(image)}-384.webp 384w`} sizes="96px" />
            <img
              src={`${thumbBase(image)}-384.webp`}
              alt={imageAlt || title || ""}
              width="600"
              height="400"
              loading="lazy"
              decoding="async"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </picture>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-white">
            {Icon ? <Icon size={26} /> : null}
          </div>
        )}
      </div>
      <div className="flex-1 min-w-0 p-4">
        {eyebrow && (
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--faro-red)]">
            {eyebrow}
          </div>
        )}
        <div className="mt-0.5 font-display font-black text-lg leading-tight text-[var(--faro-text)]">
          {title}
        </div>
        {teaser && (
          <div className="mt-1 text-xs text-[var(--faro-text-soft)] line-clamp-2">
            {teaser}
          </div>
        )}
        <div className="mt-2 text-xs font-bold text-[var(--faro-blue)] flex items-center gap-1">
          Abrir sección
          <ChevronRight size={14} />
        </div>
      </div>
    </>
  );

  const cardClass = "w-full text-left rounded-2xl bg-white border border-[rgba(30,42,143,0.14)] shadow-sm hover:shadow-md active:scale-[0.99] transition-all overflow-hidden flex items-stretch gap-0";

  return (
    <section data-testid={`mobile-fold-${id || "section"}`}>
      <div className="max-w-7xl mx-auto px-5 py-2.5">
        {to ? (
          <Link to={to} data-testid={`mobile-fold-link-${id}`} className={cardClass}>
            {CardInner}
          </Link>
        ) : (
          <div className={cardClass}>{CardInner}</div>
        )}
      </div>
    </section>
  );
}
